import { FC, useState } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { CsvUploadDropzone } from "../../../lib/company-filter/components/CsvUploadDropzone";
import { pluralize } from "../../../utils/text";
import {
  MAX_DISPLAYED_INVALID_IDS,
  MAX_MENTIONS_LIMIT,
  MENTION_CSV_TEMPLATE,
} from "./constants";
import { downloadCsv } from "./utils";

export const IdleContent: FC<{ onFilesAccepted: (files: File[]) => void }> = ({
  onFilesAccepted,
}) => (
  <Box>
    <Alert severity="info" sx={{ mb: 3 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        CSV Format Requirements
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Your CSV file must include a <strong>mention_id</strong> column.
        <br />
        Optionally include a <strong>mention_name</strong> column.
        <br />
        Maximum {MAX_MENTIONS_LIMIT.toLocaleString()} mentions per upload.
      </Typography>
      <Button
        size="small"
        onClick={() =>
          downloadCsv("mention_import_template.csv", MENTION_CSV_TEMPLATE)
        }
        sx={{ mt: 1, px: 0 }}
        data-testid="mentions-upload-template-btn"
      >
        Download Template
      </Button>
    </Alert>

    <Box data-testid="mentions-upload-dropzone">
      <CsvUploadDropzone
        id="mention-csv-upload"
        maxFiles={1}
        onFilesAccepted={onFilesAccepted}
        icon={<UploadFileIcon sx={{ fontSize: 48 }} color="secondary" />}
        title={
          <Stack spacing={0.5} alignItems="center">
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign="center"
            >
              Drag and drop your CSV file here
              <br />
              or click to browse
            </Typography>
            <Button variant="outlined">Select File</Button>
          </Stack>
        }
        helperText={null}
      />
    </Box>
  </Box>
);

export const ProcessingContent: FC = () => (
  <Stack
    alignItems="center"
    justifyContent="center"
    sx={{ py: 6 }}
    data-testid="mentions-upload-processing"
  >
    <CircularProgress size={48} sx={{ mb: 2 }} />
    <Typography variant="body1">Processing file...</Typography>
    <Typography variant="body2" color="text.secondary">
      Validating mentions against catalog
    </Typography>
  </Stack>
);

export const ErrorContent: FC<{ message: string }> = ({ message }) => (
  <Alert severity="error" sx={{ mb: 2 }} data-testid="mentions-upload-error">
    <Typography variant="subtitle2" fontWeight="bold">
      Upload Failed
    </Typography>
    <Typography variant="body2">{message}</Typography>
  </Alert>
);

export const SuccessContent: FC<{
  validCount: number;
  invalidValues: string[];
}> = ({ validCount, invalidValues }) => {
  const [showInvalidIds, setShowInvalidIds] = useState(false);
  const hasInvalid = invalidValues.length > 0;
  const remainingCount = invalidValues.length - MAX_DISPLAYED_INVALID_IDS;

  return (
    <Box>
      <Alert
        severity="success"
        sx={{ mb: 2 }}
        data-testid="mentions-upload-summary"
      >
        <Typography variant="body2">
          <strong>{validCount.toLocaleString()}</strong>{" "}
          {pluralize("valid mention", validCount)} found
          {hasInvalid && (
            <>
              , <strong>{invalidValues.length.toLocaleString()}</strong> invalid
            </>
          )}
        </Typography>
      </Alert>

      {hasInvalid && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="mentions-upload-invalid-alert"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Typography variant="body2">
              {invalidValues.length.toLocaleString()}{" "}
              {pluralize("mention ID", invalidValues.length)} could not be
              validated
            </Typography>
            <IconButton
              size="small"
              aria-label={
                showInvalidIds ? "Hide invalid IDs" : "Show invalid IDs"
              }
              onClick={() => setShowInvalidIds(!showInvalidIds)}
              data-testid="mentions-upload-invalid-toggle-btn"
            >
              {showInvalidIds ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
          <Collapse in={showInvalidIds}>
            <Box
              sx={{
                mt: 1,
                p: 1,
                bgcolor: "background.paper",
                borderRadius: 1,
                maxHeight: 150,
                overflow: "auto",
              }}
              data-testid="mentions-upload-invalid-list"
            >
              {invalidValues.slice(0, MAX_DISPLAYED_INVALID_IDS).map((id) => (
                <Typography
                  key={id}
                  variant="caption"
                  display="block"
                  sx={{ fontFamily: "monospace" }}
                >
                  {id}
                </Typography>
              ))}
              {remainingCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  ...and {remainingCount.toLocaleString()} more
                </Typography>
              )}
            </Box>
          </Collapse>
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary">
        Click Import to add the valid mentions to your selection.
      </Typography>
    </Box>
  );
};
